import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { VehicleV2 } from 'types/VehicleV2';
import ModalLeadForm from 'components/ModalLeadForm';
import { LeadFormFields } from 'types/LeadForm';
import { useLeadSubmitter } from 'lib/hooks/useLeadSender';
import { Features, SourcePages } from 'lib/EventSchema';
import { captureException } from '@sentry/nextjs';
import * as _ from 'lodash-es';
import {
  EnhancedLeadTrackingPageSource,
  EnhancedLeadTrackingPlatform,
  LeadTrackingPageSource,
} from 'types/Apis/LeadTrackingParams';
import { useAutolistUser } from 'lib/hooks/useAutolistUser';
import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import { useAlert } from 'lib/hooks/useAlert';
import useSubmitted from 'lib/hooks/useSubmitted';
import { trackLeadSuccess } from 'lib/Tracking/LeadForm/FormSuccess';
import SrpEventContext from 'contexts/SrpEventContext';
import { useAtom } from 'jotai';
import requestedPriceAtom from 'atoms/RequestedPriceAtom';
import LeadFormTrackingContext from '../Vdp/LeadFormTrackingContext';

export interface SrpLeadFormProps {
  selectedVehicle: VehicleV2;
  pagePosition?: number;
  onClose: () => void;
  onSubmitSuccess?: (submittedVehicle: VehicleV2) => void;
}

function SrpLeadForm({
  selectedVehicle,
  pagePosition,
  onClose,
  onSubmitSuccess,
}: SrpLeadFormProps): JSX.Element {
  const { t } = useTranslation();
  const { showError } = useAlert();
  const { zip } = useAutolistUser();
  const { isMobile, isTablet } = useDeviceScreen();
  const { sourcePageCategory } = useContext(SrpEventContext);
  const sourcePage =
    isMobile || isTablet ? LeadTrackingPageSource.MobileSrp : LeadTrackingPageSource.DesktopSrp;

  const [requestedPriceValues] = useAtom(requestedPriceAtom);
  const requestedPrice = requestedPriceValues[selectedVehicle.vin];
  const platform =
    isMobile || isTablet
      ? EnhancedLeadTrackingPlatform.Mobile
      : EnhancedLeadTrackingPlatform.Desktop;

  const leadSubmitter = useLeadSubmitter(
    Features.leadForm,
    sourcePage,
    EnhancedLeadTrackingPageSource.SRP,
    SourcePages.SrpLeadForm,
    platform,
    pagePosition,
    sourcePageCategory,
    requestedPrice
  );
  const leadSubmission = useSubmitted(selectedVehicle?.vin);

  async function handleSubmit({ sendAlerts, ...contactInfo }: LeadFormFields): Promise<void> {
    try {
      const { leadResponse, leadPayload } = await leadSubmitter({
        vehicle: selectedVehicle,
        contactInfo,
        sendAlerts,
      });

      trackLeadSuccess({
        vehicle: selectedVehicle,
        contactInfo,
        zip,
        leadResponse,
        geolocation: leadPayload.geolocation,
        cplValue: selectedVehicle?.cplValue,
      });

      if (onSubmitSuccess) {
        onSubmitSuccess(selectedVehicle);
      }
    } catch (e) {
      console.error(e);
      captureException(e);
      showError(t('errors.generic'), { debug: _.toPlainObject(e) });
      throw new Error('Error submitting lead');
    }
  }

  return (
    <LeadFormTrackingContext.Provider value={{ sourcePage: SourcePages.SrpLeadForm }}>
      <ModalLeadForm
        onSubmit={handleSubmit}
        onClose={onClose}
        leadSubmission={leadSubmission}
        vehiclePrice={selectedVehicle.price}
      />
    </LeadFormTrackingContext.Provider>
  );
}

export default SrpLeadForm;
